import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import PopoverProfile from './PopoverProfile';
import PopoverMachines from './PopoverMachines';
import MobileDrawer from './MobileDrawer';

import styles from './style.module.css';
import { ReactComponent as Logo } from '../../icons/logo.svg';

import { AppBar, Toolbar, IconButton, Box, useMediaQuery, useTheme, styled } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: 400,
  textDecoration: 'none',
  color: 'var(--text-primary)',
  transition: 'all 0.3s ease',
  position: 'relative',
  paddingBottom: '4px',
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: 500,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '2px',
      backgroundColor: theme.palette.primary.main,
      transform: 'scaleX(1)',
    },
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    transform: 'scaleX(0)',
    transition: 'transform 0.3s ease',
  },
  '&:hover::after': {
    transform: 'scaleX(1)',
  },
  '&:hover, &:focus': {
    color: 'var(--text-heading)',
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: 'var(--table-row-white)',
  color: 'var(--text-primary)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
}));

const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 24px',
  minHeight: '80px',
  height: '80px',
  '@media (max-width: 768px)': {
    padding: '0 16px',
    minHeight: '64px',
    height: '64px',
  },
});

function Header() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  return (
    <>
      <StyledAppBar
        position="sticky"
        elevation={0}
        style={{ boxShadow: 'none', borderBottom: '1px solid var(--table-border)' }}
      >
        <StyledToolbar>
          {/* Logo Section */}
          <Box className={styles.logoSection}>
            <NavLink to="/" className={styles.logoLink}>
              <Logo height={isMobile ? '48px' : '64px'} />
            </NavLink>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box className={styles.navigation}>
              {user?.role === 'admin' && (
                <StyledNavLink to="/users" className={({ isActive }) => (isActive ? 'active' : '')}>
                  {t('generic.users')}
                </StyledNavLink>
              )}
              {user?.role === 'admin' && (
                <StyledNavLink
                  to="/audit-logs"
                  className={({ isActive }) => (isActive ? 'active' : '')}
                >
                  {t('generic.auditLogs')}
                </StyledNavLink>
              )}
              <PopoverMachines />
              <StyledNavLink to="/" className={({ isActive }) => (isActive ? 'active' : '')}>
                {t('header.Calendar')}
              </StyledNavLink>
              <PopoverProfile />
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PopoverProfile />
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </StyledToolbar>
      </StyledAppBar>

      {/* Mobile Drawer */}
      {isMobile && (
        <MobileDrawer open={mobileDrawerOpen} onClose={handleDrawerToggle} user={user} />
      )}
    </>
  );
}

export default Header;
